import type { Swiper, SwiperEvents } from "swiper/types";

export const TallestSlideModule = ({
    swiper,
    on,
}: {
    swiper: Swiper;
    on: <E extends keyof SwiperEvents>(event: E, handler: SwiperEvents[E]) => void;
}) => {
    // Don't fight Swiper's own autoHeight
    if (swiper.params?.autoHeight) return;

    const ro = new ResizeObserver(() => schedule());
    let raf = 0;
    let lastApplied = -1;
    let ready = false; // only true after afterInit

    function getContainer(): HTMLElement | null {
        // swiper.el is set after init; don't capture it early
        return (swiper.el as HTMLElement) ?? null;
    }

    function getSlides(): HTMLElement[] {
        // Guard and filter in case of unexpected values
        const raw = (swiper.slides as unknown as any[]) || [];
        return raw.filter((n) => n && n.nodeType === 1) as HTMLElement[];
    }

    function schedule() {
        if (!ready) return; // ignore pre-init events
        if (raf) return;
        raf = requestAnimationFrame(() => {
            raf = 0;
            measureAndApply();
        });
    }

    function px(num: string | null | undefined) {
        const n = parseFloat(num || "0");
        return Number.isFinite(n) ? n : 0;
    }

    function borderBoxHeight(slide: HTMLElement) {
        const prev = slide.style.height;
        slide.style.height = ""; // remove inline to read intrinsic
        let h = slide.getBoundingClientRect().height;
        if (!h) {
            const cs = getComputedStyle(slide);
            h = slide.scrollHeight + px(cs.borderTopWidth) + px(cs.borderBottomWidth);
        }
        slide.style.height = prev; // restore
        return Math.round(h);
    }

    function measureAndApply() {
        if (!ready) return;

        const container = getContainer();
        if (!container) return;

        const slides = getSlides();
        if (!slides.length) return;

        let max = 0;
        for (const s of slides) {
            // Defensive: skip if somehow not an element
            if (!(s instanceof HTMLElement)) continue;
            const h = borderBoxHeight(s);
            if (h > max) max = h;
        }

        if (max && max !== lastApplied) {
            container.style.height = `${max}px`; // lock container height
            lastApplied = max;
        }
    }

    function observeSlides() {
        ro.disconnect();
        const slides = getSlides();
        slides.forEach((s) => ro.observe(s));
    }

    on("afterInit", () => {
        ready = true;

        // Now it's safe to query
        const container = getContainer();
        if (container) {
            // Watch late-loading images once
            container.querySelectorAll("img").forEach((img) => {
                const el = img as HTMLImageElement;
                if (!el.complete) {
                    el.addEventListener("load", schedule, { once: true });
                    el.addEventListener("error", schedule, { once: true });
                }
            });
        }

        observeSlides();

        // Fonts finishing can change layout too
        // (ts-ignore to avoid older DOM lib complaints)
        document.fonts?.ready?.then?.(() => schedule());

        schedule();
    });

    // Valid v11 events. All guarded by `ready`.
    on("resize", schedule);
    on("breakpoint", () => {
        if (!ready) return;
        observeSlides();
        schedule();
    });
    on("slidesUpdated", schedule);
    on("slidesLengthChange", () => {
        if (!ready) return;
        observeSlides();
        schedule();
    });
    on("observerUpdate", () => {
        if (!ready) return;
        observeSlides();
        schedule();
    });
    on("update", schedule);

    on("destroy", () => {
        ro.disconnect();
        cancelAnimationFrame(raf);
        const container = getContainer();
        if (container) container.style.height = "";
        ready = false;
    });
};
